import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X, User, MapPin, Briefcase, CreditCard, Building2, Save, Loader2 } from 'lucide-react';
import apiClient from '../api/client';
import { CustomerDetailDto, UpdateKycDetailsDto } from '../types/dtos';

interface KYCEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    customer: CustomerDetailDto;
    onSaved: (updated: Partial<CustomerDetailDto>) => void;
}

type Tab = 'personal' | 'address' | 'banking' | 'corporate';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'personal', label: 'Personal', icon: <User size={15} /> },
    { key: 'address', label: 'Address & Employment', icon: <MapPin size={15} /> },
    { key: 'banking', label: 'Banking', icon: <CreditCard size={15} /> },
    { key: 'corporate', label: 'Corporate', icon: <Building2 size={15} /> },
];

const GENDER_OPTIONS = ['', 'Male', 'Female', 'Other', 'Prefer not to say'];
const MARITAL_OPTIONS = ['', 'Single', 'Married', 'Divorced', 'Widowed', 'Separated'];

function Field({
    label,
    children,
    colSpan = 1,
}: {
    label: string;
    children: React.ReactNode;
    colSpan?: number;
}) {
    return (
        <div className={colSpan === 2 ? 'col-span-2' : ''}>
            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">
                {label}
            </label>
            {children}
        </div>
    );
}

const inputCls =
    'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white';

export default function KYCEditModal({ isOpen, onClose, customer, onSaved }: KYCEditModalProps) {
    const [activeTab, setActiveTab] = useState<Tab>('personal');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state — mirrors UpdateKycDetailsDto
    const [form, setForm] = useState<UpdateKycDetailsDto>({});

    // Populate form fields from current customer whenever the modal opens
    useEffect(() => {
        if (isOpen) {
            setActiveTab('personal');
            setError(null);
            setForm({
                firstName: customer.firstName,
                lastName: customer.lastName,
                phoneNumber: customer.phoneNumber,
                nationalId: customer.nationalId,
                passport: customer.passport,
                tpin: customer.tpin,
                dateOfBirth: customer.dateOfBirth
                    ? new Date(customer.dateOfBirth).toISOString().slice(0, 10)
                    : '',
                gender: customer.gender,
                nationality: customer.nationality,
                maritalStatus: customer.maritalStatus,
                physicalAddress: customer.physicalAddress,
                postalAddress: (customer as any).postalAddress ?? '',
                city: customer.city,
                country: customer.country,
                occupation: customer.occupation,
                sourceOfFunds: (customer as any).sourceOfFunds ?? '',
                bankName: customer.bankName,
                accountNo: customer.accountNo,
                registrationNo: customer.registrationNo,
                dateOfIncorporation: customer.dateOfIncorporation
                    ? new Date(customer.dateOfIncorporation).toISOString().slice(0, 10)
                    : '',
                organisationType: customer.organisationType,
                industry: customer.industry,
                contactPerson: customer.contactPerson,
                businessLocation: customer.businessLocation,
            });
        }
    }, [isOpen, customer]);

    const set = (key: keyof UpdateKycDetailsDto) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
            setForm(prev => ({ ...prev, [key]: e.target.value }));

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        // Clean payload: convert empty strings to null for date fields to avoid 400 Errors in .NET
        const payload = { ...form };
        if (payload.dateOfBirth === '') payload.dateOfBirth = undefined;
        if (payload.dateOfIncorporation === '') payload.dateOfIncorporation = undefined;

        console.log('[KYC Edit] Saving payload:', payload);

        try {
            await apiClient.put(`/admin/customers/${customer.id}/kyc`, payload);
            // Build partial update to reflect in parent without reload
            onSaved({
                firstName: form.firstName,
                lastName: form.lastName,
                phoneNumber: form.phoneNumber,
                nationalId: form.nationalId,
                passport: form.passport,
                tpin: form.tpin,
                dateOfBirth: form.dateOfBirth,
                gender: form.gender,
                nationality: form.nationality,
                maritalStatus: form.maritalStatus,
                physicalAddress: form.physicalAddress,
                city: form.city,
                country: form.country,
                occupation: form.occupation,
                bankName: form.bankName,
                accountNo: form.accountNo,
                registrationNo: form.registrationNo,
                dateOfIncorporation: form.dateOfIncorporation,
                organisationType: form.organisationType,
                industry: form.industry,
                contactPerson: form.contactPerson,
                businessLocation: form.businessLocation,
            });
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save KYC details. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose}>

                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                {/* Panel */}
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">

                                {/* Top accent */}
                                <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500" />

                                {/* Header */}
                                <div className="flex items-center justify-between px-6 pt-5 pb-3">
                                    <div>
                                        <Dialog.Title className="text-lg font-bold text-gray-900">
                                            Edit KYC Details
                                        </Dialog.Title>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            {customer.firstName} {customer.lastName}
                                        </p>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                {/* Tabs */}
                                <div className="flex border-b border-gray-200 px-6">
                                    {TABS.map(tab => (
                                        <button
                                            key={tab.key}
                                            onClick={() => setActiveTab(tab.key)}
                                            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold tracking-wide border-b-2 -mb-px transition-colors ${activeTab === tab.key
                                                ? 'border-blue-700 text-blue-800'
                                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            {tab.icon}
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Tab Content */}
                                <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">

                                    {/* ── Personal ── */}
                                    {activeTab === 'personal' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <Field label="First Name">
                                                <input className={inputCls} value={form.firstName ?? ''} onChange={set('firstName')} />
                                            </Field>
                                            <Field label="Last Name">
                                                <input className={inputCls} value={form.lastName ?? ''} onChange={set('lastName')} />
                                            </Field>
                                            <Field label="Phone Number">
                                                <input className={inputCls} value={form.phoneNumber ?? ''} onChange={set('phoneNumber')} />
                                            </Field>
                                            <Field label="National ID">
                                                <input className={inputCls} value={form.nationalId ?? ''} onChange={set('nationalId')} />
                                            </Field>
                                            <Field label="Passport / Driver's Licence">
                                                <input className={inputCls} value={form.passport ?? ''} onChange={set('passport')} />
                                            </Field>
                                            <Field label="TPIN">
                                                <input className={inputCls} value={form.tpin ?? ''} onChange={set('tpin')} />
                                            </Field>
                                            <Field label="Date of Birth">
                                                <input type="date" className={inputCls} value={form.dateOfBirth ?? ''} onChange={set('dateOfBirth')} />
                                            </Field>
                                            <Field label="Gender">
                                                <select className={inputCls} value={form.gender ?? ''} onChange={set('gender')}>
                                                    {GENDER_OPTIONS.map(o => (
                                                        <option key={o} value={o}>{o || '— Select —'}</option>
                                                    ))}
                                                </select>
                                            </Field>
                                            <Field label="Nationality">
                                                <input className={inputCls} value={form.nationality ?? ''} onChange={set('nationality')} />
                                            </Field>
                                            <Field label="Marital Status">
                                                <select className={inputCls} value={form.maritalStatus ?? ''} onChange={set('maritalStatus')}>
                                                    {MARITAL_OPTIONS.map(o => (
                                                        <option key={o} value={o}>{o || '— Select —'}</option>
                                                    ))}
                                                </select>
                                            </Field>
                                        </div>
                                    )}

                                    {/* ── Address & Employment ── */}
                                    {activeTab === 'address' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <Field label="Physical Address" colSpan={2}>
                                                <textarea
                                                    rows={2}
                                                    className={inputCls}
                                                    value={form.physicalAddress ?? ''}
                                                    onChange={set('physicalAddress')}
                                                />
                                            </Field>
                                            <Field label="Postal Address" colSpan={2}>
                                                <textarea
                                                    rows={2}
                                                    className={inputCls}
                                                    value={form.postalAddress ?? ''}
                                                    onChange={set('postalAddress')}
                                                />
                                            </Field>
                                            <Field label="City">
                                                <input className={inputCls} value={form.city ?? ''} onChange={set('city')} />
                                            </Field>
                                            <Field label="Country">
                                                <input className={inputCls} value={form.country ?? ''} onChange={set('country')} />
                                            </Field>
                                            <Field label="Occupation">
                                                <input className={inputCls} value={form.occupation ?? ''} onChange={set('occupation')} />
                                            </Field>
                                            <Field label="Source of Funds">
                                                <input className={inputCls} value={form.sourceOfFunds ?? ''} onChange={set('sourceOfFunds')} />
                                            </Field>
                                        </div>
                                    )}

                                    {/* ── Banking ── */}
                                    {activeTab === 'banking' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <Field label="Bank Name">
                                                <input className={inputCls} value={form.bankName ?? ''} onChange={set('bankName')} />
                                            </Field>
                                            <Field label="Account Number">
                                                <input className={inputCls} value={form.accountNo ?? ''} onChange={set('accountNo')} />
                                            </Field>
                                        </div>
                                    )}

                                    {/* ── Corporate ── */}
                                    {activeTab === 'corporate' && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <Field label="Organisation Type">
                                                <input className={inputCls} value={form.organisationType ?? ''} onChange={set('organisationType')} />
                                            </Field>
                                            <Field label="Registration No.">
                                                <input className={inputCls} value={form.registrationNo ?? ''} onChange={set('registrationNo')} />
                                            </Field>
                                            <Field label="Industry">
                                                <input className={inputCls} value={form.industry ?? ''} onChange={set('industry')} />
                                            </Field>
                                            <Field label="Contact Person">
                                                <input className={inputCls} value={form.contactPerson ?? ''} onChange={set('contactPerson')} />
                                            </Field>
                                            <Field label="Business Location" colSpan={2}>
                                                <input className={inputCls} value={form.businessLocation ?? ''} onChange={set('businessLocation')} />
                                            </Field>
                                            <Field label="Date of Incorporation">
                                                <input type="date" className={inputCls} value={form.dateOfIncorporation ?? ''} onChange={set('dateOfIncorporation')} />
                                            </Field>
                                        </div>
                                    )}

                                    {error && (
                                        <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                            {error}
                                        </p>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row-reverse gap-3">
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="flex items-center justify-center gap-2 bg-blue-900 hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-all"
                                    >
                                        {saving ? (
                                            <><Loader2 size={16} className="animate-spin" /> Saving…</>
                                        ) : (
                                            <><Save size={16} /> Save Changes</>
                                        )}
                                    </button>
                                    <button
                                        onClick={onClose}
                                        disabled={saving}
                                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>

                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
